import React, { useState } from "react";
import { v4 as uuid } from "uuid";
import { useLocation, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { formValidator } from "../../../../helpers";
import { Modal, Button, InputGroup } from "../../../../UI";
import { createPlans, updatePlans } from "../../../../redux/actions";
import Container from "./style";

const ModalContent = ({ action, plans }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { gymLocations } = useSelector((s) => s.dashStats);
  const activePlanData =
    location.state && location.state.id
      ? plans.find((item) => item.id === location.state.id)
      : {};
  const [loading, setLoading] = useState(false);
  const [formData, setFormState] = useState(() =>
    action === "edit"
      ? {
          ...activePlanData,
          // Normalize boolean/null/undefined → 0 or 1 so controlled selects
          // always have a matching <option value={0|1}>.
          is_shareable: Number(activePlanData.is_shareable ?? 0),
          can_earn_referral_bonus: Number(
            activePlanData.can_earn_referral_bonus ?? 0
          ),
          prerequisite_plan_id: activePlanData.prerequisite_plan_id
            ? Number(activePlanData.prerequisite_plan_id)
            : null,
        }
      : {
          name: "",
          amount: 0,
          id: uuid(),
          interval: "Year",
          allow_pause_duration: 0,
          description: "",
          duration: 0,
          location_id: 1,
          is_shareable: 0,
          shareable_count: 0,
          can_earn_referral_bonus: 0,
          prerequisite_plan_id: null,
        }
  );

  const handleInput = ({ target: { name, value } }) => {
    setFormState((s) => ({ ...s, [name]: value }));
  };

  // For selects whose options are numeric (0 / 1) we must cast to Number
  // so the controlled `value` prop always matches an option correctly.
  const handleNumericSelect = ({ target: { name, value } }) => {
    setFormState((s) => ({ ...s, [name]: Number(value) }));
  };

  const handleLocationChange = ({ target: { value } }) => {
    const newLocationId = Number(value);
    setFormState((s) => {
      const selectedPrereq = plans?.find((p) => String(p.id) === String(s.prerequisite_plan_id));
      const isPrereqValid = selectedPrereq && Number(selectedPrereq.location_id) === newLocationId;
      return {
        ...s,
        location_id: newLocationId,
        prerequisite_plan_id: isPrereqValid ? s.prerequisite_plan_id : null,
      };
    });
  };

  const handleSubmit = async () => {
    if (
      formValidator(document.forms["class-form"].getElementsByTagName("input"))
    ) {
      try {
        setLoading(true);
        const response =
          action === "edit"
            ? await dispatch(updatePlans({ ...formData }))
            : await dispatch(createPlans({ ...formData }));
        if (response && response.success === true) {
          history.goBack();
        } else {
          setLoading(false);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Container>
      <Modal
        showModal={true}
        modalTitle={
          action === "edit" ? `Edit ${activePlanData.name} Plan` : "New Plans"
        }
        modalFooter={
          <Button form="class-form" loading={loading} full type="submit">
            {action === "edit" ? "Save" : "Create Plan"}
          </Button>
        }
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          id="class-form"
          name="class-form"
          noValidate
        >
          <InputGroup
            label="Name"
            name="name"
            required={true}
            onChange={handleInput}
            value={formData.name}
          />
          <InputGroup
            label="Amount"
            type="number"
            name="amount"
            required={true}
            onChange={handleInput}
            value={formData.amount}
          />
          <InputGroup>
            <label>Interval</label>
            <select
              name="interval"
              required={true}
              onChange={handleInput}
              value={formData.interval}
            >
              <option value="Day">Day</option>
              <option value={"Week"}>Week</option>
              <option value={"Month"}>Month</option>
              <option value={"Quarter"}>Quarter</option>
              <option value={"Year"}>Year</option>
            </select>
          </InputGroup>

          <InputGroup>
            <label>Location</label>
            <select
              name="location_id"
              required={true}
              onChange={handleLocationChange}
              value={formData.location_id}
            >
              {Object.keys(gymLocations).map((locationId) => (
                <option value={locationId} key={locationId}>{gymLocations[locationId]}</option>
              ))}
            </select>
          </InputGroup>

          <InputGroup
            label="Duration"
            onChange={handleInput}
            value={formData.duration}
            type="number"
            name="duration"
            required={true}
          />
          <InputGroup
            label="Allow Pause Duration"
            onChange={handleInput}
            value={formData.allow_pause_duration}
            type="number"
            name="allow_pause_duration"
          />
          <InputGroup>
            <label>Description</label>
            <textarea
              name="description"
              required={true}
              value={formData.description}
              onChange={handleInput}
            />
          </InputGroup>

          <InputGroup>
            <label>Is Shareable</label>
            <select
              name="is_shareable"
              onChange={handleNumericSelect}
              value={Number(formData.is_shareable)}
            >
              <option value={0}>No</option>
              <option value={1}>Yes</option>
            </select>
          </InputGroup>

          {Number(formData.is_shareable) === 1 && (
            <InputGroup
              label="Shareable Count"
              type="number"
              name="shareable_count"
              onChange={handleInput}
              value={formData.shareable_count}
              required={true}
            />
          )}

          <InputGroup>
            <label>Supports Referral Bonus</label>
            <select
              name="can_earn_referral_bonus"
              onChange={handleNumericSelect}
              value={Number(formData.can_earn_referral_bonus)}
            >
              <option value={0}>No</option>
              <option value={1}>Yes</option>
            </select>
          </InputGroup>

          <InputGroup>
            <label>Prerequisite Plan (Required Active Plan)</label>
            <select
              name="prerequisite_plan_id"
              onChange={(e) => {
                const val = e.target.value;
                setFormState((s) => ({
                  ...s,
                  prerequisite_plan_id: val ? Number(val) : null,
                }));
              }}
              value={formData.prerequisite_plan_id || ""}
            >
              <option value="">None (No Prerequisite)</option>
              {plans &&
                plans
                  .filter(
                    (item) =>
                      String(item.id) !== String(formData.id) &&
                      Number(item.location_id) === Number(formData.location_id)
                  )
                  .map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
            </select>
          </InputGroup>
        </form>
      </Modal>
    </Container>
  );
};

export default ModalContent;
